/**
 * OrderHandoff.tsx — the clinic-handoff chips and detail section for Axiom
 * peptide orders (SCRUM-2200): payment ($, read-only), Reconstituted (every
 * Axiom order) and Picked up (orders delivered to the clinic only).
 *
 * The toggle state lives in useOrderHandoff(), owned by the orders table so the
 * row chips and the detail modal show the same optimistic value.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Package } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/services/apiClient';
import {
  setOrderHandoff,
  type IntegrationOrder,
  type OrderHandoff,
  type OrderHandoffStep,
} from '@/services/integrations.service';
import { toast } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import {
  canToggleHandoff,
  orderHandoff,
  paymentChip,
  shipsToClinic,
  shortDate,
  stampTip,
  type PaymentChipTone,
} from '@/lib/orderHandoff';

export const STEP_LABELS: Record<OrderHandoffStep, string> = {
  reconstituted: 'Reconstituted',
  pickedUp: 'Picked up',
};

const TONE_VARIANT: Record<PaymentChipTone, 'warning' | 'success' | 'neutral'> = {
  amber: 'warning',
  green: 'success',
  slate: 'neutral',
};

interface Override {
  handoff: OrderHandoff;
  /** The server's value when the toggle started — a refetch that changes it retires the override. */
  base: string;
}

export interface OrderHandoffControls {
  handoffFor: (order: IntegrationOrder) => OrderHandoff;
  isPending: (order: IntegrationOrder, step: OrderHandoffStep) => boolean;
  toggle: (order: IntegrationOrder, step: OrderHandoffStep) => Promise<void>;
}

/**
 * Optimistic handoff toggles for a list of orders. A click shows its result at
 * once; the server's answer replaces it; a failure rolls the step back and
 * says why. Overrides retire as soon as fresh order data arrives.
 */
export function useOrderHandoff(orders: IntegrationOrder[], fallbackPatientId: number): OrderHandoffControls {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [overrides, setOverrides] = useState<Record<number, Override>>({});
  const [pending, setPending] = useState<Record<string, true>>({});

  // Fresh data (a refetch after the toggle, or another user's change) wins
  // over a settled override.
  useEffect(() => {
    setOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const o of orders) {
        const ov = next[o.integrationOrderId];
        if (!ov) continue;
        const busy = Object.keys(pending).some((k) => k.startsWith(`${o.integrationOrderId}:`));
        if (busy) continue;
        const now = JSON.stringify(orderHandoff(o));
        if (now !== ov.base || now === JSON.stringify(ov.handoff)) {
          delete next[o.integrationOrderId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orders, pending]);

  const handoffFor = useCallback(
    (o: IntegrationOrder) => overrides[o.integrationOrderId]?.handoff ?? orderHandoff(o),
    [overrides],
  );
  const isPending = useCallback(
    (o: IntegrationOrder, step: OrderHandoffStep) => !!pending[`${o.integrationOrderId}:${step}`],
    [pending],
  );

  const toggle = useCallback(async (o: IntegrationOrder, step: OrderHandoffStep) => {
    const id = o.integrationOrderId;
    const key = `${id}:${step}`;
    if (pending[key]) return;
    const current = overrides[id]?.handoff ?? orderHandoff(o);
    const original = current[step];
    const done = !original;
    const base = overrides[id]?.base ?? JSON.stringify(orderHandoff(o));
    const byName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || null : null;

    const withStep = (h: OrderHandoff, value: OrderHandoff[OrderHandoffStep] | undefined): OrderHandoff => {
      const n = { ...h };
      if (value) n[step] = value; else delete n[step];
      return n;
    };

    setOverrides((prev) => ({
      ...prev,
      [id]: { base, handoff: withStep(prev[id]?.handoff ?? current, done ? { at: new Date().toISOString(), byName } : undefined) },
    }));
    setPending((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await setOrderHandoff(o.patientId ?? fallbackPatientId, id, step, done);
      setOverrides((prev) => ({
        ...prev,
        // The server's stamp for this step (its time and name), keeping any
        // other step still in flight as the user left it.
        [id]: { base, handoff: withStep(prev[id]?.handoff ?? current, res.handoff?.[step]) },
      }));
      void queryClient.invalidateQueries({
        predicate: (q) => {
          const k = String(q.queryKey[0] ?? '');
          return (k.startsWith('patient-') || k.startsWith('account-')) && k.endsWith('-orders');
        },
      });
      void queryClient.invalidateQueries({ queryKey: ['integration-order-events', o.patientId ?? fallbackPatientId, id] });
    } catch (e) {
      setOverrides((prev) => ({
        ...prev,
        [id]: { base, handoff: withStep(prev[id]?.handoff ?? current, original) },
      }));
      toast.error(`Could not update "${STEP_LABELS[step]}"`, getErrorMessage(e));
    } finally {
      setPending((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
    }
  }, [pending, overrides, user, fallbackPatientId, queryClient]);

  return { handoffFor, isPending, toggle };
}

/** The read-only $ chip, with its explanation on hover. Nothing when the order has no charge. */
export function PaymentChipBadge({ order }: { order: IntegrationOrder }) {
  const chip = paymentChip(order);
  if (!chip) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} data-testid="handoff-payment" className="inline-flex focus-visible:outline-none">
          <Badge size="sm" variant={TONE_VARIANT[chip.tone]}>${' '}{chip.label}</Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{chip.tip}</TooltipContent>
    </Tooltip>
  );
}

function StepChip({ order, step, controls }: { order: IntegrationOrder; step: OrderHandoffStep; controls: OrderHandoffControls }) {
  const label = STEP_LABELS[step];
  const stamp = controls.handoffFor(order)[step];
  const busy = controls.isPending(order, step);
  const disabled = !canToggleHandoff(order);
  const Icon = step === 'reconstituted' ? FlaskConical : Package;
  const tip = disabled
    ? `${label} — not available on a cancelled order`
    : stamp
      ? `${stampTip(label, stamp)} — click to undo`
      : `Not ${label.toLowerCase()} yet — click to mark`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={stamp ? `${label}: done — undo` : `${label}: mark done`}
          aria-pressed={!!stamp}
          disabled={busy || disabled}
          onClick={(e) => { e.stopPropagation(); void controls.toggle(order, step); }}
          className={`inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full px-1.5 text-2xs font-medium ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ${
            stamp
              ? 'bg-green-50 text-green-700 ring-green-600/20 hover:bg-green-100'
              : 'bg-white text-slate-500 ring-slate-300 hover:bg-slate-50 hover:text-slate-700'
          }`}
        >
          <Icon className="h-3 w-3 shrink-0" aria-hidden />
          {stamp ? `✓ ${shortDate(stamp.at)}` : '—'}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{tip}</TooltipContent>
    </Tooltip>
  );
}

/** The table's Clinic cell: $ · Reconstituted · Picked up (clinic deliveries only). */
export function HandoffChips({ order, controls }: { order: IntegrationOrder; controls: OrderHandoffControls }) {
  return (
    <div className="flex items-center gap-1" data-testid="handoff-chips">
      <PaymentChipBadge order={order} />
      <StepChip order={order} step="reconstituted" controls={controls} />
      {shipsToClinic(order) && <StepChip order={order} step="pickedUp" controls={controls} />}
    </div>
  );
}

const longWhen = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** The detail modal's "Clinic handoff" rows: payment, then each step with Mark / Undo. */
export function HandoffSection({ order, controls }: { order: IntegrationOrder; controls: OrderHandoffControls }) {
  const chip = paymentChip(order);
  const handoff = controls.handoffFor(order);
  const steps: OrderHandoffStep[] = shipsToClinic(order) ? ['reconstituted', 'pickedUp'] : ['reconstituted'];
  const toggleable = canToggleHandoff(order);
  return (
    <div className="space-y-1" data-testid="handoff-section">
      <div className="flex items-center justify-between gap-6 py-1 text-sm">
        <span className="shrink-0 text-slate-500">Payment</span>
        <span className="text-right text-slate-900">
          {chip ? (
            <>
              <Badge size="sm" variant={TONE_VARIANT[chip.tone]}>{chip.label}</Badge>
              {chip.label === 'Paid' && order.paidAt && shortDate(order.paidAt) && (
                <span className="ml-2 text-xs text-slate-500">{longWhen(order.paidAt)}</span>
              )}
            </>
          ) : '—'}
        </span>
      </div>
      {steps.map((step) => {
        const stamp = handoff[step];
        return (
          <div key={step} className="flex items-center justify-between gap-6 py-1 text-sm">
            <span className="shrink-0 text-slate-500">{STEP_LABELS[step]}</span>
            <span className="flex items-center gap-3 text-right text-slate-900">
              <span>
                {stamp ? `${longWhen(stamp.at)}${stamp.byName ? ` by ${stamp.byName}` : ''}` : <span className="text-slate-400">Not yet</span>}
              </span>
              {toggleable && (
                <Button
                  size="xs"
                  variant="secondary"
                  disabled={controls.isPending(order, step)}
                  onClick={() => void controls.toggle(order, step)}
                  aria-label={stamp ? `Undo ${STEP_LABELS[step].toLowerCase()}` : `Mark ${STEP_LABELS[step].toLowerCase()}`}
                >
                  {stamp ? 'Undo' : 'Mark'}
                </Button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
